import { useEffect, useMemo, useState } from 'react';
import type { GanttTask } from '../types/gantt';
import type { Gardener } from '../data/gardeners';
import type { Machine } from '../data/machines';
import { getMachines } from '../data/machines';
import { useI18n } from '../i18n';
import TaskLegalBadge from './TaskLegalBadge';
import { getTaskLegalMeta } from '../utils/taskLegalStatus';
import {
	countWorkdaysBetweenCal,
	endDateForWorkingDays,
	type CompanyCalendar
} from '../utils/ganttDataGenerator';
import { isCompanyWorkingDay, nextCompanyWorkingDay } from '../utils/companySchedule';
import { normalizeCompanyCalendar } from '../utils/ganttDataGenerator';
import { toTimelineDateIso } from '../utils/dateUtils';

type GanttChartProps = {
	tasks: GanttTask[];
	gardeners?: Gardener[];
	machines?: Machine[];
	startDate?: Date;
	endDate?: Date;
	editable?: boolean;
	showPlanned?: boolean; // for project detail: show planned timeline
	plannedTasks?: GanttTask[]; // planned tasks (grayed out)
	onTaskUpdate?: (taskId: string, updates: Partial<GanttTask>) => void | Promise<void>;
	onTaskClick?: (task: GanttTask) => void; // Click handler for tasks
	className?: string;
	companyCountryCode?: string | null;
	compactLegalBadge?: boolean;
	/** Compact read-only strip: fixed week view, no zoom/filters, smaller rows (e.g. project plan quick view) */
	preview?: boolean;
	/** When set, task duration edits use working days (company schedule) instead of calendar days. */
	companyCalendar?: CompanyCalendar | null;
};

type ZoomLevel = 'day' | 'week' | 'month';
type ViewMode = 'by-gardener' | 'all-tasks';
type ResourceFilter = 'people' | 'machines' | 'both';

function startOfWeekMonday(date: Date): Date {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	// JS: 0=Sun...6=Sat. Convert so Monday=0..Sunday=6
	const day = (d.getDay() + 6) % 7;
	d.setDate(d.getDate() - day);
	return d;
}

function startOfMonth(date: Date): Date {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	d.setDate(1);
	return d;
}

function endOfMonth(date: Date): Date {
	const d = new Date(date);
	d.setHours(23, 59, 59, 999);
	// Day 0 of next month = last day of current month
	d.setMonth(d.getMonth() + 1, 0);
	return d;
}

function parseTaskDate(value: string): Date {
	const dateOnly = value.split('T')[0];
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
		return new Date(`${dateOnly}T12:00:00`);
	}
	return new Date(value);
}

function localDayKey(date: Date): number {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / (1000 * 60 * 60 * 24);
}

export default function GanttChart({
	tasks,
	gardeners = [],
	machines: providedMachines,
	startDate,
	endDate,
	editable = false,
	showPlanned = false,
	plannedTasks = [],
	onTaskUpdate,
	onTaskClick,
	className = '',
	companyCountryCode,
	compactLegalBadge = false,
	preview = false,
	companyCalendar = null
}: GanttChartProps) {
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) => (language === 'de' ? de : language === 'hr' ? hr : en);
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const [zoomLevelState, setZoomLevel] = useState<ZoomLevel>('day');
	/** Plan preview: use day columns so short multi-step schedules don't collapse into one week column. */
	const zoomLevel: ZoomLevel = preview ? 'day' : zoomLevelState;
	const [viewModeState, setViewMode] = useState<ViewMode>('all-tasks');
	const viewMode: ViewMode = preview ? 'all-tasks' : viewModeState;
	const [resourceFilterState, setResourceFilter] = useState<ResourceFilter>('both');
	const resourceFilter: ResourceFilter = preview ? 'both' : resourceFilterState;
	const [selectedTask, setSelectedTask] = useState<string | null>(null);
	const [editingTask, setEditingTask] = useState<GanttTask | null>(null);
	const [editForm, setEditForm] = useState<{ startDate: string; endDate: string; duration: number } | null>(null);
	
	// Get machines (use provided or load all)
	const allMachines = useMemo(() => {
		return providedMachines || getMachines();
	}, [providedMachines]);

	// Rows follow schedule order (start date), not creation/insertion order — new or replanned tasks must not sit on top.
	const displayTasks = useMemo(() => {
		const startMs = (t: GanttTask) => {
			const n = new Date(t.startDate).getTime();
			return Number.isFinite(n) ? n : 0;
		};
		const endMs = (t: GanttTask) => {
			const n = new Date(t.endDate).getTime();
			return Number.isFinite(n) ? n : 0;
		};
		return [...tasks].sort((a, b) => {
			const da = startMs(a);
			const db = startMs(b);
			if (da !== db) return da - db;
			return endMs(a) - endMs(b);
		});
	}, [tasks]);

	// Hide machine controls unless tasks actually use machines (machines not offered yet)
	const hasMachineTasks = useMemo(() => {
		return tasks.some(t => Array.isArray(t.machineIds) && t.machineIds.length > 0) ||
			plannedTasks.some(t => Array.isArray(t.machineIds) && t.machineIds.length > 0);
	}, [tasks, plannedTasks]);

	useEffect(() => {
		if (!hasMachineTasks && resourceFilter === 'machines') {
			setResourceFilter('both');
		}
	}, [hasMachineTasks, resourceFilter]);
	
	// Task date range (no padding) and padded range (used for day/week so chart doesn't feel cramped)
	const taskDateRange = useMemo(() => {
		if (startDate && endDate) {
			return { start: new Date(startDate), end: new Date(endDate) };
		}

		const allTasks = [...tasks, ...plannedTasks];
		if (allTasks.length === 0) {
			const today = new Date();
			return { start: today, end: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000) };
		}

		const dates = allTasks.flatMap(t => [new Date(t.startDate), new Date(t.endDate)]);
		const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
		const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
		return { start: minDate, end: maxDate };
	}, [tasks, plannedTasks, startDate, endDate]);

	const paddedDateRange = useMemo(() => {
		const minDate = new Date(taskDateRange.start);
		const maxDate = new Date(taskDateRange.end);
		minDate.setDate(minDate.getDate() - 2);
		maxDate.setDate(maxDate.getDate() + 2);
		return { start: minDate, end: maxDate };
	}, [taskDateRange]);

	// Axis range: month view should snap to whole months of the actual tasks (avoid March/May when tasks are all April).
	const axisDateRange = useMemo(() => {
		if (zoomLevel === 'month') {
			return {
				start: startOfMonth(taskDateRange.start),
				end: endOfMonth(taskDateRange.end)
			};
		}
		return paddedDateRange;
	}, [zoomLevel, taskDateRange, paddedDateRange]);
	
	// Filter tasks based on resource filter
	const filteredTasks = useMemo(() => {
		if (resourceFilter === 'both') return displayTasks;
		
		return displayTasks.filter(task => {
			const hasGardeners = (task.gardenerIds && task.gardenerIds.length > 0) || task.gardenerId;
			const hasMachines = task.machineIds && task.machineIds.length > 0;
			
			if (resourceFilter === 'people') {
				return hasGardeners;
			} else if (resourceFilter === 'machines') {
				return hasMachines;
			}
			return true;
		});
	}, [displayTasks, resourceFilter]);
	
	// Get unique gardeners from tasks (support both single and multiple gardener assignments)
	const taskGardeners = useMemo(() => {
		const gardenerMap = new Map<string, Gardener>();
		filteredTasks.forEach(task => {
			// Support both old format (gardenerId) and new format (gardenerIds)
			const gardenerIds = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
			gardenerIds.forEach(gardenerId => {
				if (gardenerId && !gardenerMap.has(gardenerId)) {
					const gardener = gardeners.find(g => g.id === gardenerId);
					if (gardener) {
						gardenerMap.set(gardenerId, gardener);
					}
				}
			});
		});
		return Array.from(gardenerMap.values());
	}, [filteredTasks, gardeners]);
	
	// Get unique machines from tasks
	const taskMachines = useMemo(() => {
		const machineMap = new Map<string, Machine>();
		filteredTasks.forEach(task => {
			if (task.machineIds) {
				task.machineIds.forEach(machineId => {
					if (machineId && !machineMap.has(machineId)) {
						const machine = allMachines.find(m => m.id === machineId);
						if (machine) {
							machineMap.set(machineId, machine);
						}
					}
				});
			}
		});
		return Array.from(machineMap.values());
	}, [filteredTasks, allMachines]);
	
	// Group tasks by resource (gardener/machine) or show as flat list
	const groupedTasks = useMemo(() => {
		if (viewMode === 'by-gardener' && taskGardeners.length > 0 && resourceFilter !== 'machines') {
			// Group by gardener - tasks with multiple gardeners appear under each gardener
			return taskGardeners.map(gardener => ({
				resource: { type: 'gardener' as const, data: gardener },
				tasks: filteredTasks.filter(t => {
					const taskGardenerIds = t.gardenerIds || (t.gardenerId ? [t.gardenerId] : []);
					return taskGardenerIds.includes(gardener.id);
				})
			}));
		} else if (resourceFilter === 'machines' && taskMachines.length > 0) {
			// Group by machine
			return taskMachines.map(machine => ({
				resource: { type: 'machine' as const, data: machine },
				tasks: filteredTasks.filter(t => {
					return t.machineIds?.includes(machine.id) || false;
				})
			}));
		}
		// Flat list - show each task only once with all resources listed
		return [{ resource: null, tasks: filteredTasks }];
	}, [filteredTasks, taskGardeners, taskMachines, viewMode, resourceFilter]);
	
	// Generate time axis labels (also used to determine chart width for true zooming)
	const timeAxisLabels = useMemo(() => {
		const labels: Array<{ date: Date; label: string }> = [];
		const start = new Date(axisDateRange.start);
		const end = new Date(axisDateRange.end);
		start.setHours(0, 0, 0, 0);
		end.setHours(0, 0, 0, 0);

		if (zoomLevel === 'day') {
			const cur = new Date(start);
			while (cur <= end) {
				labels.push({
					date: new Date(cur),
					label: cur.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
				});
				cur.setDate(cur.getDate() + 1);
			}
			return labels;
		}

		if (zoomLevel === 'week') {
			const cur = startOfWeekMonday(start);
			let weekIndex = 1;
			while (cur <= end) {
				const weekStart = new Date(cur);
				const weekEnd = new Date(cur);
				weekEnd.setDate(weekEnd.getDate() + 6);
				const rangeLabel = `${weekStart.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}–${weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}`;
				labels.push({
					date: new Date(cur),
					label: `${copy('Week', 'Woche', 'Tjedan')} ${weekIndex} · ${rangeLabel}`
				});
				weekIndex++;
				cur.setDate(cur.getDate() + 7);
			}
			return labels;
		}

		// month
		const cur = startOfMonth(start);
		while (cur <= end) {
			labels.push({
				date: new Date(cur),
				label: cur.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
			});
			cur.setMonth(cur.getMonth() + 1);
		}
		return labels;
	}, [axisDateRange, zoomLevel]);

	const axisCellWidthPx = useMemo(() => {
		if (preview) {
			switch (zoomLevel) {
				case 'day': return 40;
				case 'week': return 82;
				case 'month': return 102;
				default: return 100;
			}
		}
		switch (zoomLevel) {
			case 'day': return 48;
			case 'week': return 104;
			case 'month': return 136;
			default: return 140;
		}
	}, [zoomLevel, preview]);

	const chartWidthPx = useMemo(() => {
		return Math.max(600, timeAxisLabels.length * axisCellWidthPx);
	}, [timeAxisLabels.length, axisCellWidthPx]);

	const resolvedCalendar = useMemo(
		() => (companyCalendar ? normalizeCompanyCalendar(companyCalendar) : null),
		[companyCalendar]
	);

	const isNonWorkingChartDay = (date: Date) => {
		if (!resolvedCalendar || zoomLevel !== 'day') return false;
		return !isCompanyWorkingDay(
			date,
			resolvedCalendar.workingDays,
			resolvedCalendar.nonWorkingDates
		);
	};

	// Calculate bar positions by calendar unit, not raw timestamp ratio.
	const getDatePositionPx = (dateStr: string, isEnd = false): number => {
		if (timeAxisLabels.length === 0) return 0;

		const date = parseTaskDate(dateStr);
		const axisStart = timeAxisLabels[0].date;
		const totalCells = Math.max(1, timeAxisLabels.length);
		const maxPx = totalCells * axisCellWidthPx;

		let unitIndex = 0;
		if (zoomLevel === 'day') {
			unitIndex = localDayKey(date) - localDayKey(axisStart);
		} else if (zoomLevel === 'week') {
			const weekStart = startOfWeekMonday(axisStart);
			unitIndex = Math.floor((localDayKey(date) - localDayKey(weekStart)) / 7);
		} else {
			const startMonthIndex = axisStart.getFullYear() * 12 + axisStart.getMonth();
			const dateMonthIndex = date.getFullYear() * 12 + date.getMonth();
			unitIndex = dateMonthIndex - startMonthIndex;
		}

		const effectiveIndex = isEnd ? unitIndex + 1 : unitIndex;
		return Math.max(0, Math.min(maxPx, effectiveIndex * axisCellWidthPx));
	};
	
	const getStatusColor = (status?: string): string => {
		switch (status) {
			case 'completed': return '#198754';
			case 'in_progress': return '#ffc107';
			case 'at_risk': return '#ffc107';
			case 'delayed': return '#dc3545';
			case 'blocked': return '#dc3545';
			default: return '#E1F1E7'; // Pastel light green matching background
		}
	};
	
	// Handle task editing
	const handleEditTask = (task: GanttTask) => {
		const start = parseTaskDate(task.startDate);
		const end = parseTaskDate(task.endDate);
		const workingDays =
			typeof task.duration === 'number' && task.duration > 0
				? task.duration
				: countWorkdaysBetweenCal(start, end, companyCalendar);
		setEditingTask(task);
		setEditForm({
			startDate: task.startDate.split('T')[0],
			endDate: task.endDate.split('T')[0],
			duration: Math.max(1, workingDays)
		});
	};
	
	const snapToWorkingDay = (date: Date) => {
		if (companyCalendar) {
			return nextCompanyWorkingDay(date, companyCalendar.workingDays, companyCalendar.nonWorkingDates);
		}
		const d = new Date(date);
		while (d.getDay() === 0 || d.getDay() === 6) {
			d.setDate(d.getDate() + 1);
		}
		return d;
	};

	const handleSaveTaskEdit = async () => {
		if (!editingTask || !editForm || !onTaskUpdate) return;

		const start = snapToWorkingDay(new Date(editForm.startDate));
		const workingDays = Math.max(1, editForm.duration);
		const end = endDateForWorkingDays(start, workingDays, companyCalendar);

		try {
			await Promise.resolve(
				onTaskUpdate(editingTask.id, {
					startDate: toTimelineDateIso(start),
					endDate: toTimelineDateIso(end),
					duration: workingDays
				})
			);
		} catch (e) {
			console.error('Failed to save task from Gantt:', e);
			return;
		}

		setEditingTask(null);
		setEditForm(null);
	};
	
	const labelColWidth = preview ? 260 : 220;
	const taskRowHeight = preview ? 44 : 56;
	const dynamicRowsCount = groupedTasks.reduce((sum, group) => sum + group.tasks.length, 0);
	const dynamicHeightPx = Math.min(760, Math.max(300, dynamicRowsCount * taskRowHeight + 110));
	const chartScrollMaxHeight = preview ? 'none' : `${dynamicHeightPx}px`;
	const axisHeaderPadding = preview ? '8px 12px' : '12px 16px';

	return (
		<div className={`gantt-chart ${className}`}>
			{/* Controls */}
			{!preview && (
			<div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
				<div className="btn-group" role="group">
					<button
						type="button"
						className={`btn btn-sm ${zoomLevel === 'day' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setZoomLevel('day')}
					>
						{copy('Day', 'Tag', 'Dan')}
					</button>
					<button
						type="button"
						className={`btn btn-sm ${zoomLevel === 'week' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setZoomLevel('week')}
					>
						{copy('Week', 'Woche', 'Tjedan')}
					</button>
					<button
						type="button"
						className={`btn btn-sm ${zoomLevel === 'month' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setZoomLevel('month')}
					>
						{copy('Month', 'Monat', 'Mjesec')}
					</button>
				</div>
				{hasMachineTasks && (
					<div className="btn-group" role="group">
						<button
							type="button"
							className={`btn btn-sm ${resourceFilter === 'people' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setResourceFilter('people')}
							title={copy('Show only tasks with people assigned', 'Nur Aufgaben mit zugewiesenen Personen anzeigen', 'Prikaži samo zadatke s dodijeljenim osobama')}
						>
							{copy('People', 'Personen', 'Osobe')}
						</button>
						<button
							type="button"
							className={`btn btn-sm ${resourceFilter === 'machines' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setResourceFilter('machines')}
							title={copy('Show only tasks with machines assigned', 'Nur Aufgaben mit zugewiesenen Maschinen anzeigen', 'Prikaži samo zadatke s dodijeljenim strojevima')}
						>
							{copy('Machines', 'Maschinen', 'Strojevi')}
						</button>
						<button
							type="button"
							className={`btn btn-sm ${resourceFilter === 'both' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setResourceFilter('both')}
							title={copy('Show all tasks', 'Alle Aufgaben anzeigen', 'Prikaži sve zadatke')}
						>
							{copy('Both', 'Oboje', 'Oboje')}
						</button>
					</div>
				)}
				{(taskGardeners.length > 0 || taskMachines.length > 0) && resourceFilter !== 'machines' && (
					<div className="btn-group" role="group">
						<button
							type="button"
							className={`btn btn-sm ${viewMode === 'all-tasks' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setViewMode('all-tasks')}
							title={copy('Show each task once with all resources', 'Jede Aufgabe einmal mit allen Ressourcen anzeigen', 'Prikaži svaki zadatak jednom sa svim resursima')}
						>
							{copy('All Tasks', 'Alle Aufgaben', 'Svi zadaci')}
						</button>
						{taskGardeners.length > 0 && (
							<button
								type="button"
								className={`btn btn-sm ${viewMode === 'by-gardener' ? 'btn-success' : 'btn-outline-secondary'}`}
								onClick={() => setViewMode('by-gardener')}
								title={copy('Group tasks by gardener', 'Aufgaben nach Gärtner gruppieren', 'Grupiraj zadatke po vrtlaru')}
							>
								{copy('By Gardener', 'Nach Gärtner', 'Po vrtlaru')}
							</button>
						)}
					</div>
				)}
			</div>
			)}
			
			{/* Gantt Chart */}
			<div className={preview ? '' : 'card'}>
				<div className={preview ? '' : 'card-body p-0'}>
					<div style={{ overflowX: 'auto', overflowY: preview ? 'visible' : 'auto', maxHeight: chartScrollMaxHeight, backgroundColor: '#f8f9fa' }}>
						{/* Time Axis */}
						<div className="position-sticky top-0 bg-light border-bottom" style={{ zIndex: 10 }}>
							<div className="d-flex" style={{ minWidth: `${labelColWidth + chartWidthPx}px` }}>
								<div style={{ width: `${labelColWidth}px`, minWidth: `${labelColWidth}px`, padding: axisHeaderPadding, borderRight: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
									<div className="fw-semibold text-uppercase text-muted" style={{ fontSize: preview ? '10px' : '11px', letterSpacing: '0.5px' }}>{preview ? copy('Task', 'Aufgabe', 'Zadatak') : copy('Task / Resource', 'Aufgabe / Ressource', 'Zadatak / Resurs')}</div>
								</div>
								<div className="flex-grow-1 d-flex" style={{ minWidth: `${chartWidthPx}px` }}>
									{timeAxisLabels.map((label, idx) => (
										<div
											key={`label-${idx}`}
											className="border-end text-center small d-flex align-items-center justify-content-center"
											style={{ 
												minWidth: `${axisCellWidthPx}px`,
												padding: preview ? '8px 4px' : '12px 8px',
												backgroundColor: isNonWorkingChartDay(label.date) ? '#e9ecef' : '#f8f9fa',
												fontWeight: 500,
												color: isNonWorkingChartDay(label.date) ? '#868e96' : '#495057',
												fontSize: preview ? '11px' : undefined
											}}
											title={
												isNonWorkingChartDay(label.date)
													? copy('Non-working day', 'Arbeitsfreier Tag', 'Neradni dan')
													: undefined
											}
										>
											{zoomLevel === 'day' ? (
												<div className="d-flex flex-column align-items-center justify-content-center" style={{ lineHeight: 1.1 }}>
													<span>{label.date.toLocaleDateString(locale, { day: 'numeric' })}</span>
													<span>{label.date.toLocaleDateString(locale, { month: 'short' })}</span>
												</div>
											) : (
												label.label
											)}
										</div>
									))}
								</div>
							</div>
						</div>
						
						{/* Planned Tasks (if showing) */}
						{showPlanned && plannedTasks.length > 0 && (
							<div className="border-bottom" style={{ opacity: 0.3 }}>
								{plannedTasks.map(task => {
									const left = getDatePositionPx(task.startDate, false);
									const width = getDatePositionPx(task.endDate, true) - left;
									
									return (
										<div
											key={`planned-${task.id}`}
											className="position-relative"
											style={{ height: preview ? '32px' : '40px', minWidth: `${labelColWidth + chartWidthPx}px` }}
										>
											<div
												className="position-absolute bg-secondary"
												style={{
													left: `${left}px`,
													width: `${Math.max(4, width)}px`,
													top: '50%',
													transform: 'translateY(-50%)',
													height: '24px',
													borderRadius: '4px'
												}}
												title={`Planned: ${task.name}`}
											/>
										</div>
									);
								})}
							</div>
						)}
						
						{/* Tasks by Resource */}
						{groupedTasks.map((group, groupIdx) => (
							<div
								key={`group-${groupIdx}`}
								className="border-bottom position-relative"
								style={{
									borderColor: '#e9ecef',
									minWidth: `${labelColWidth + chartWidthPx}px`,
									backgroundColor: '#f8f9fa'
								}}
							>
								{zoomLevel === 'day' && resolvedCalendar && (
									<div
										className="position-absolute top-0 bottom-0 d-flex"
										style={{ left: `${labelColWidth}px`, zIndex: 0, pointerEvents: 'none' }}
										aria-hidden
									>
										{timeAxisLabels.map((label, idx) => (
											<div
												key={`nw-col-${idx}`}
												style={{
													minWidth: `${axisCellWidthPx}px`,
													width: `${axisCellWidthPx}px`,
													backgroundColor: isNonWorkingChartDay(label.date)
														? 'rgba(233, 236, 239, 0.85)'
														: 'transparent'
												}}
											/>
										))}
									</div>
								)}
								{group.tasks.map((task, taskIdx) => {
									const left = getDatePositionPx(task.startDate, false);
									const width = getDatePositionPx(task.endDate, true) - left;
									const color = getStatusColor(task.status);
									const progress = task.progress || 0;
									const legalMeta = getTaskLegalMeta(task, companyCountryCode);
									
									return (
										<div
											key={task.id}
											className={`position-relative d-flex gantt-task-row ${preview ? 'align-items-stretch' : 'align-items-center'}`}
											style={{
												minHeight: `${taskRowHeight}px`,
												height: preview ? 'auto' : `${taskRowHeight}px`,
												minWidth: `${labelColWidth + chartWidthPx}px`,
												transition: 'background-color 0.15s ease',
												backgroundColor: '#f8f9fa'
											}}
											onClick={() => {
												if (editable && onTaskUpdate) {
													// In editable mode, clicking opens edit dialog
													handleEditTask(task);
												} else if (onTaskClick) {
													onTaskClick(task);
												} else {
													setSelectedTask(selectedTask === task.id ? null : task.id);
												}
											}}
										>
											{/* Task Name */}
											<div
												className={`border-end d-flex flex-column ${preview ? 'justify-content-center px-2 py-2' : 'px-3 py-2 justify-content-center'}`}
												style={{
													width: `${labelColWidth}px`,
													minWidth: `${labelColWidth}px`,
													fontSize: preview ? '12px' : '13px',
													backgroundColor: '#fff',
													...(preview ? { wordBreak: 'break-word', overflowWrap: 'anywhere' as const } : {})
												}}
											>
												<div className={`d-flex ${preview ? 'align-items-start' : 'align-items-center'} gap-2`}>
													{!preview && task.isCritical && (
														<span className="badge text-bg-success" style={{ fontSize: '10px' }}>
															{copy('Critical', 'Kritično', 'Kritično')}
														</span>
													)}
													{!preview && task.hasPtoInRange && (
														<span className="badge text-bg-warning text-dark" style={{ fontSize: '10px' }} title={copy('Task dates extended to skip assignee time off', 'Termine wurden verlängert, um Abwesenheiten zu überspringen', 'Datumi zadataka produljeni su kako bi se preskočila odsutnost dodijeljenih osoba')}>
															PTO
														</span>
													)}
													{!preview && (
														compactLegalBadge ? (
															legalMeta ? (
																<span
																	className="badge text-bg-warning text-dark"
																	title={legalMeta.reason}
																	style={{ fontSize: '10px', minWidth: '18px' }}
																>
																	!
																</span>
															) : null
														) : (
															<TaskLegalBadge legalMeta={legalMeta} copy={copy} className="align-self-start" />
														)
													)}
													<span className="fw-semibold" style={preview ? { lineHeight: 1.35 } : undefined}>{task.name}</span>
												</div>
												{!preview && (viewMode === 'all-tasks' ? (
													// Show all resources for this task
													<div className="small text-muted">
														{(() => {
															const parts: string[] = [];
															
															// Gardeners
															const taskGardenerIds = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
															if (taskGardenerIds.length > 0) {
																const taskGardeners = taskGardenerIds
																	.map(id => gardeners.find(g => g.id === id))
																	.filter(Boolean) as Gardener[];
																parts.push(`👤 ${taskGardeners.map(g => g.name).join(', ')}`);
															}
															
															// Machines
															if (task.machineIds && task.machineIds.length > 0) {
																const taskMachines = task.machineIds
																	.map(id => allMachines.find(m => m.id === id))
																	.filter(Boolean) as Machine[];
																parts.push(`🔧 ${taskMachines.map(m => m.name).join(', ')}`);
															}
															
															return parts.length > 0 ? parts.join(' | ') : copy('Unassigned', 'Nicht zugewiesen', 'Nije dodijeljeno');
														})()}
													</div>
												) : group.resource ? (
													// Show current resource
													<div className="small text-muted">
														{group.resource.type === 'gardener' ? (
															<>
																👤 {group.resource.data.name}
																{(() => {
																	const taskGardenerIds = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
																	if (taskGardenerIds.length > 1) {
																		const otherCount = taskGardenerIds.filter(id => id !== group.resource!.data.id).length;
																		return ` + ${otherCount} other${otherCount > 1 ? 's' : ''}`;
																	}
																	return '';
																})()}
															</>
														) : (
															<>🔧 {group.resource.data.name}</>
														)}
													</div>
												) : null)}
											</div>
											
											{/* Task Bar */}
											<div
												className="flex-grow-1 position-relative align-self-stretch"
												style={{
													minHeight: preview ? `${taskRowHeight}px` : undefined,
													padding: preview ? '6px 4px' : '8px 4px',
													backgroundColor: '#f8f9fa'
												}}
											>
												<div
													className={`gantt-task-bar position-absolute ${
														task.status === 'completed' ? 'gantt-task-completed' :
														task.status === 'in_progress' ? 'gantt-task-in-progress' :
														task.status === 'at_risk' ? 'gantt-task-at-risk' :
														task.status === 'delayed' || task.status === 'blocked' ? 'gantt-task-delayed' :
														'gantt-task-not-started'
													} ${task.isCritical && !preview ? 'gantt-task-critical' : ''}`}
													style={{
														left: `${left}px`,
														width: `${Math.max(8, width)}px`,
														top: '50%',
														transform: 'translateY(-50%)',
														height: preview ? '26px' : '36px',
														backgroundColor: color,
														borderRadius: '6px',
														border: task.isCritical && !preview ? '2px solid #198754' : '1px solid rgba(0,0,0,0.15)',
														cursor: editable ? 'pointer' : (onTaskClick ? 'pointer' : 'default'),
														opacity: task.status === 'completed' ? 0.85 : 1,
														minWidth: '24px'
													}}
													title={editable ? `Click to edit: ${task.name}` : `${task.name} (${task.duration} days, ${progress}% complete)${task.machineIds && task.machineIds.length > 0 ? ` - Machines: ${task.machineIds.map(id => allMachines.find(m => m.id === id)?.name).filter(Boolean).join(', ')}` : ''}`}
												>
													{/* Progress indicator */}
													{progress > 0 && (
														<div
															className="gantt-progress-bar position-absolute"
															style={{
																width: `${progress}%`,
																height: '100%',
																left: 0,
																top: 0,
																borderRadius: '6px 0 0 6px',
																opacity: 0.4,
																backgroundColor: 'rgba(255, 255, 255, 0.4)'
															}}
														/>
													)}
													{/* Task label */}
													{width > 8 && (
														<div 
															className="position-absolute top-50 start-50 translate-middle small d-flex align-items-center justify-content-center" 
															style={{ 
																fontSize: '11px', 
																fontWeight: 600,
																whiteSpace: 'nowrap',
																color: task.status === 'not_started' || !task.status ? '#198754' : 
																       task.status === 'completed' ? '#157347' :
																       task.status === 'in_progress' || task.status === 'at_risk' ? '#cc9900' :
																       task.status === 'delayed' || task.status === 'blocked' ? '#bb2d3b' : '#198754',
																textShadow: 'none',
																pointerEvents: 'none'
															}}
														>
															{task.duration}d
														</div>
													)}
												</div>
												
												{/* Dependencies */}
												{!preview && task.dependencies && task.dependencies.map((depId, depIdx) => {
													const depTask = displayTasks.find(t => t.id === depId);
													if (!depTask) return null;
													
													const depEnd = getDatePositionPx(depTask.endDate, true);
													const taskStart = getDatePositionPx(task.startDate, false);
													// Skip rendering when tasks overlap/backtrack; avoids misleading reverse arrows.
													if (depEnd >= taskStart) return null;
													
													return (
														<svg
															key={`dep-${depIdx}`}
															className="position-absolute"
															style={{
																left: 0,
																top: 0,
																width: '100%',
																height: '100%',
																pointerEvents: 'none',
																zIndex: 2,
																overflow: 'visible'
															}}
														>
															<defs>
																<marker 
																	id={`arrowhead-${task.id}-${depIdx}`} 
																	markerWidth="10" 
																	markerHeight="10" 
																	refX="9" 
																	refY="3" 
																	orient="auto"
																	markerUnits="strokeWidth"
																>
																	<polygon points="0 0, 10 3, 0 6" fill="#198754" />
																</marker>
															</defs>
															<line
																x1={depEnd + 4}
																y1="50%"
																x2={taskStart - 4}
																y2="50%"
																stroke="#198754"
																strokeWidth="2"
																strokeDasharray="4 3"
																markerEnd={`url(#arrowhead-${task.id}-${depIdx})`}
																className="gantt-dependency-line"
																opacity="0.65"
															/>
														</svg>
													);
												})}
											</div>
										</div>
									);
								})}
							</div>
						))}
					</div>
				</div>
			</div>
			
			{/* Legend */}
			{preview ? (
				<p className="small text-muted mb-0 mt-2">
					{copy('Quick view', 'Schnellansicht', 'Brzi prikaz')} — {copy('names and dates only.', 'nur Namen und Daten.', 'samo nazivi i datumi.')} {copy('Open the', 'Öffnen Sie den Reiter', 'Otvorite karticu')} <strong>{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</strong> {copy('tab for people, dependencies, and editing.', 'für Personen, Abhängigkeiten und Bearbeitung.', 'za osobe, ovisnosti i uređivanje.')}
				</p>
			) : className.includes('hide-status-legend') ? null : (
			<div className="mt-3">
				<div className="d-flex flex-wrap gap-3 align-items-center small">
					<span className="fw-semibold">{copy('Status:', 'Status:', 'Status:')}</span>
					<span><span className="badge text-bg-secondary">{copy('Not Started', 'Nicht gestartet', 'Nije započeto')}</span></span>
					<span><span className="badge text-bg-warning">{copy('In Progress / At Risk', 'In Bearbeitung / Risiko', 'U tijeku / rizik')}</span></span>
					<span><span className="badge text-bg-success">{copy('Completed', 'Abgeschlossen', 'Završeno')}</span></span>
					<span><span className="badge text-bg-danger">{copy('Delayed / Blocked', 'Verzögert / Blockiert', 'Kasni / blokirano')}</span></span>
					<span className="ms-3"><span className="badge text-bg-success">{copy('Critical Path', 'Kritischer Pfad', 'Kritični put')}</span></span>
					{editable && (
						<span className="ms-3 text-muted">
							<i className="bi bi-info-circle me-1"></i>
							{copy('Double-click a task to edit dates', 'Doppelklick auf eine Aufgabe zum Bearbeiten der Daten', 'Dvokliknite zadatak za uređivanje datuma')}
						</span>
					)}
				</div>
			</div>
			)}
			
			{/* Edit Task Modal */}
			{editingTask && editForm && (
				<div
					className="modal show d-block"
					style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 21000 }}
					tabIndex={-1}
				>
					<div className="modal-dialog">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{copy('Edit Task:', 'Aufgabe bearbeiten:', 'Uredi zadatak:')} {editingTask.name}</h5>
								<button
									type="button"
									className="btn-close"
									onClick={() => {
										setEditingTask(null);
										setEditForm(null);
									}}
								></button>
							</div>
							<div className="modal-body">
								<div className="mb-3">
									<label className="form-label">{copy('Start Date', 'Startdatum', 'Datum početka')}</label>
									<input
										type="date"
										className="form-control"
										value={editForm.startDate}
										onChange={(e) => {
											const newStart = snapToWorkingDay(new Date(e.target.value));
											const startStr = newStart.toISOString().split('T')[0];
											const end = endDateForWorkingDays(newStart, editForm.duration, companyCalendar);
											setEditForm({
												...editForm,
												startDate: startStr,
												endDate: end.toISOString().split('T')[0],
												duration: editForm.duration
											});
										}}
									/>
								</div>
								<div className="mb-3">
									<label className="form-label">{copy('End Date', 'Enddatum', 'Datum završetka')}</label>
									<input
										type="date"
										className="form-control"
										value={editForm.endDate}
										onChange={(e) => {
											const start = snapToWorkingDay(new Date(editForm.startDate));
											const end = snapToWorkingDay(new Date(e.target.value));
											const workingDays = companyCalendar
												? countWorkdaysBetweenCal(start, end, companyCalendar)
												: Math.max(
														1,
														Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
													);
											setEditForm({
												...editForm,
												endDate: end.toISOString().split('T')[0],
												duration: Math.max(1, workingDays)
											});
										}}
									/>
								</div>
								<div className="mb-3">
									<label className="form-label">
										{companyCalendar
											? copy('Duration (working days)', 'Dauer (Arbeitstage)', 'Trajanje (radni dani)')
											: copy('Duration (days)', 'Dauer (Tage)', 'Trajanje (dani)')}
									</label>
									<input
										type="number"
										className="form-control"
										min={1}
										value={editForm.duration}
									onChange={(e) => {
										const days = parseInt(e.target.value) || 1;
										const start = snapToWorkingDay(new Date(editForm.startDate));
										const end = endDateForWorkingDays(start, days, companyCalendar);
										setEditForm({
											...editForm,
											duration: Math.max(1, days),
											startDate: start.toISOString().split('T')[0],
											endDate: end.toISOString().split('T')[0]
										});
									}}
									/>
									{companyCalendar && (
										<div className="form-text">
											{copy(
												'Weekends and company non-working days extend the calendar span on the chart.',
												'Wochenenden und betriebliche freie Tage verlängern den Kalenderzeitraum im Diagramm.',
												'Vikendi i neradni dani tvrtke produžuju kalendarski raspon na grafikonu.'
											)}
										</div>
									)}
								</div>
							</div>
							<div className="modal-footer">
								<button
									type="button"
									className="btn btn-secondary"
									onClick={() => {
										setEditingTask(null);
										setEditForm(null);
									}}
								>
									{copy('Cancel', 'Abbrechen', 'Odustani')}
								</button>
								<button
									type="button"
									className="btn btn-primary"
									onClick={handleSaveTaskEdit}
								>
									{copy('Save Changes', 'Änderungen speichern', 'Spremi promjene')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

